import _ from 'lodash-es';
import { logger, trackEvent } from '@tt4b/ttam-utils';
import { computed, Ref } from '@vue/composition-api';
import {
  AcoFormData,
  ACTION_TYPE,
  AdgroupFormData,
  FullNormalFormData,
  OfflineEventsObjectives,
  OfflineEventsProductSource,
  OfflineEventsPromotionFlowType,
  OffLineEvents,
} from '@tt4b/ttam-enums';
import { useCreativeActionTypeIs, getOfflineEventsInfoByIds } from '@tt4b/ttam-hooks';
import { useOfflineEventsList } from './useOfflineEventsList';

/**
 * 过滤掉已被删除的离线事件，并返回新的事件ID数组和被删除的事件ID数组。
 * @param {string[]} initialEventSetIds - 初始的事件ID数组
 * @param {Object[]} originalEventsList - 原始的完整事件列表，包含事件ID和其他信息
 * @returns {Object} 返回新的事件ID数组和被删除的事件ID数组
 */
const filterDeletedEvents = (initialEventSetIds: string[] = [], originalEventsList: readonly OffLineEvents[] = []) => {
  // 从完整事件列表中提取所有的ID
  const originalEventIds = originalEventsList.map(event => event?.id || '');

  // 判断哪些ID已被删除（不在完整事件列表的ID数组中）
  const deletedIds = initialEventSetIds.filter(id => !originalEventIds.includes(id));

  // 从初始的事件ID数组中移除被删除的ID，得到新的事件ID数组
  const newEventSetIds = initialEventSetIds.filter(id => !deletedIds.includes(id));

  // 返回新的事件ID数组和被删除的事件ID数组
  return {
    newEventSetIds,
    deletedIds,
  };
};
/**
 * 在创建/复制场景下，增加所有自动跟踪的ID到离线事件集ID数组中
 * @param {Array} eventSetIds - 离线事件集ID数组
 * @param {Array} autoTrackingEvents - 自动跟踪事件列表
 * @returns {Array} 返回添加了自动跟踪事件后的新离线事件集ID数组
 */
const addAutoTrackingEvents = (eventSetIds: string[], autoTrackingEvents: OffLineEvents[]) => {
  const autoEventIds = autoTrackingEvents.map(event => event?.id || '') || [];
  autoEventIds.forEach(id => {
    if (!eventSetIds.includes(id)) {
      eventSetIds.push(id);
    }
  });
  return eventSetIds;
};

// 这个函数用于获取最终展示到页面上的离线事件和已经被删除的事件信息
export const useOfflineEventsDefaultData = (opt: {
  createMode: Ref<typeof ACTION_TYPE[keyof typeof ACTION_TYPE]>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) => {
  const { createMode, creativeData, adgroupData } = opt;
  const { isEdit, isCreate, isCopy, isAcoEdit } = useCreativeActionTypeIs({
    createMode,
    adgroupFormData: adgroupData,
  });
  // 是否显示这个模块
  const isShowOfflineEvents = computed(
    () =>
      OfflineEventsObjectives.includes(adgroupData.value?.objectiveType) &&
      OfflineEventsProductSource.includes(adgroupData.value?.productSource) &&
      OfflineEventsPromotionFlowType.includes(adgroupData.value?.promotionFlowType),
  );
  const { oriAutoTrackingEventsList, oriOfflineEventsList, hasLoaded, loadOfflineEventsList } = useOfflineEventsList();

  const { trackingOfflineEventSetIds = [] } = creativeData.value;
  // 获取最终应该显示到页面上的离线事件
  const getOfflineEventsDefaultSelectedData = async () => {
    let newTrackingOfflineEventSetIds = _.cloneDeep(trackingOfflineEventSetIds);
    let deletedIds: string[] = [];
    let deletedNames: string[] = [];
    // 过滤一下creativeData的trackingOfflineEventSetIds数组中是否有events已被删除
    const filteredResult = filterDeletedEvents(trackingOfflineEventSetIds, oriOfflineEventsList.value);
    // 更新trackingOfflineEventSetIds
    newTrackingOfflineEventSetIds = filteredResult.newEventSetIds;

    // 草稿和编辑场景
    if ((isEdit.value || isCreate.value) && filteredResult.deletedIds.length) {
      // 获取已删除的事件ID列表
      deletedIds = filteredResult.deletedIds;
      const deletedItems = await getOfflineEventsInfoByIds({
        eventIds: deletedIds,
      });
      deletedNames = deletedItems.map(item => item.name);
    }

    // 新建/复制场景下，默认选中所有的auto tracking
    const isShouldSetScene = (isCreate.value && !isAcoEdit.value) || isCopy.value;
    if (isShouldSetScene && oriAutoTrackingEventsList.value?.length) {
      newTrackingOfflineEventSetIds = addAutoTrackingEvents(
        newTrackingOfflineEventSetIds,
        oriAutoTrackingEventsList.value,
      );
    }

    return {
      trackingOfflineEventSetIds: newTrackingOfflineEventSetIds,
      deletedIds,
      deletedNames,
    };
  };
  // 加载所有的离线事件
  const fetchOfflineEventsConfig = async () => {
    if (!isShowOfflineEvents.value) {
      return Promise.resolve({});
    }
    logger.info('tracking_offline_events_init');
    try {
      if (!hasLoaded.value) {
        await loadOfflineEventsList();
        logger.info('tracking_offline_events_loadOfflineEventsList');

        return Promise.resolve({});
      }

      return Promise.resolve({});
    } catch (err) {
      logger.error('tracking_offline_events_loadOfflineEventsList');

      return {};
    }
  };

  // 更新 trackingOfflineEventConfig
  const getOfflineEventsDefalutData = async () => {
    const trackingOfflineEventConfig = {
      deletedIds: [],
      serverTrackingOfflineEventSetIds: trackingOfflineEventSetIds,
      deletedNames: [],
    };
    const result = {
      trackingOfflineEventConfig,
    };

    const {
      trackingOfflineEventSetIds: newTrackingOfflineEventSetIds,
      deletedIds,
      deletedNames,
    } = await getOfflineEventsDefaultSelectedData();

    Object.assign(result, {
      trackingOfflineEventSetIds: newTrackingOfflineEventSetIds,
    });

    // 草稿和编辑场景
    if (isEdit.value || isCreate.value) {
      trackingOfflineEventConfig.deletedIds = deletedIds;
      trackingOfflineEventConfig.deletedNames = deletedNames;
    }

    // 上报埋点
    if (oriAutoTrackingEventsList.value?.length) {
      setTimeout(() => {
        trackEvent('auto_offline_show_default', { number: oriAutoTrackingEventsList.value?.length ?? 0 });
      }, 0);
    }

    return result;
  };

  // 用于初始化
  const fetchHandler = () => fetchOfflineEventsConfig().then(getOfflineEventsDefalutData);

  return {
    isShowOfflineEvents,
    fetchHandler,
    getOfflineEventsDefaultSelectedData,
  };
};

export default useOfflineEventsDefaultData;
